import { AppText, AppIcon } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppDropDown from "@toothfairy/shared-ui/AppDropDown";
import AppForms from "@toothfairy/shared-ui/AppForms";
import AppTextInput from "@toothfairy/shared-ui/AppTextInput";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import React, { useEffect } from "react";
import IdCopyView from "../../../../Components/ToothFairyComponents/IdCopyView";
import { useTranslation } from "react-i18next";
import { View } from "react-native";
import ToothFairyButton from "../../../../Components/ToothFairyComponents/ToothFairyButton";
import Constants from "../../../../Constants";
import {
  getNativeEvents,
  getProviders,
} from "../../../../Constants/triggerCatalog";
import { isLargeScreen } from "../../../MainViewContainer";
import { useHistory } from "../../../../Router";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import useSections from "../../../../Hooks/useSections";
import {
  BuilderSection,
  PassthroughSection,
  useBuilderDirtyTracking,
} from "./BuilderSections";

const TRIGGER_SOURCE_OPTIONS = [
  { id: "NATIVE", name: "Native (ToothFairyAI events)" },
  { id: "EXTERNAL", name: "External (3rd-party app)" },
];

const TRIGGER_SECTION_FIELD_MAP = {
  profile: ["name", "description"],
  execution: ["agentID", "forcedPrompt", "customPromptID"],
  event: ["eventSourceType", "native", "external"],
  behaviour: ["isActive"],
};

const FILTER_OPS = [
  { id: "eq", name: "equals" },
  { id: "neq", name: "not equals" },
  { id: "includes", name: "includes" },
  { id: "gt", name: "greater than" },
  { id: "lt", name: "less than" },
  { id: "exists", name: "exists" },
];

const TriggerSettingsView = ({
  isEdit,
  handleDeletion,
  isBuilderLayout,
  builderActiveSection,
  onBuilderContextChange,
  saveVersion,
}) => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const history = useHistory();
  const { getActiveWorkspace } = useWorkspaces();
  const { setDisplayedView } = useSections();
  const { setFieldValue, values, initialValues, errors, submitCount } =
    AppForms.useFormikContext();
  const { state } = AppStateManager.useAppStateManager();

  const _showBuilderSection = (sectionId) =>
    builderActiveSection == null || builderActiveSection === sectionId;
  const SectionRenderer = isBuilderLayout ? BuilderSection : PassthroughSection;

  useBuilderDirtyTracking({
    isBuilderLayout,
    values,
    initialValues,
    saveVersion,
    onBuilderContextChange,
    errors,
    submitCount,
    sectionFieldMap: TRIGGER_SECTION_FIELD_MAP,
  });

  const isNative = values?.eventSourceType === "NATIVE";

  const agents = state?.agents?.filter((u) => u?.mode != "voice") || [];
  const customPrompts = state?.customPrompts || [];
  const authorisations = state?.authorisations || [];

  // Single source of truth: catalog comes from the backend
  // (triggerCatalogApi → backend-core-1/integrations/triggers/catalog.js) and is
  // loaded into Redux by TriggersList. Fall back to the in-memory cache for the
  // first paint.
  const nativeEvents =
    state?.triggerCatalog?.nativeEvents?.length > 0
      ? state.triggerCatalog.nativeEvents
      : getNativeEvents();
  const providers =
    state?.triggerCatalog?.providers?.length > 0
      ? state.triggerCatalog.providers
      : getProviders();

  const getAvailablePrompts = () => {
    const selectedAgentId = values?.agentID;
    if (!selectedAgentId) return customPrompts;
    return customPrompts.filter((prompt) => {
      if (!prompt.availableToAgents || prompt.availableToAgents.length === 0) {
        return true;
      }
      return prompt.availableToAgents.includes(selectedAgentId);
    });
  };

  useEffect(() => {
    if (!isEdit && values?.agentID) {
      const availablePrompts = getAvailablePrompts();
      const currentPromptStillAvailable = availablePrompts.find(
        (p) => p.id === values?.customPromptID
      );
      if (!currentPromptStillAvailable && values?.customPromptID) {
        setFieldValue("customPromptID", "");
      }
    }
  }, [values?.agentID]);

  useEffect(() => {
    if (isNative && !values?.native?.eventType && nativeEvents.length > 0) {
      setFieldValue("native.eventType", nativeEvents[0].id);
      setFieldValue("native.eventCategory", nativeEvents[0].category);
    }
  }, [isNative]);

  // Derive the selected provider from the SAME `providers` array we render in
  // the dropdown (from Redux / cache). Using the module-level findProvider() here
  // would read a separate in-memory cache that may still be empty on first load,
  // causing the events dropdown to stay blank until the user re-selects.
  const selectedProvider = providers.find(
    (p) => p.id === values?.external?.provider
  );
  const providerEvents = selectedProvider?.events || [];

  // Show ALL authorisations the user has configured in ToothFairyAI — no static
  // type filtering. The selected authorisation's own type is persisted as
  // external.authorisationType on save.
  const authorisationOptions = [
    { id: "", name: t("triggers.selectAuthorisation") },
    ...authorisations.map((a) => ({ id: a.id, name: a.name })),
  ];

  const _title = () =>
    isEdit ? t("settings.list.save") : t("settings.list.create");

  const getTriggerObject = () =>
    state?.triggers?.find((u) => u?.id === values?.id);

  const addFilterRow = () => {
    const current = values?.native?.dataFilter || [];
    setFieldValue("native.dataFilter", [
      ...current,
      { field: "", op: "eq", value: "" },
    ]);
  };

  const updateFilterRow = (index, key, value) => {
    const current = [...(values?.native?.dataFilter || [])];
    current[index] = { ...current[index], [key]: value };
    setFieldValue("native.dataFilter", current);
  };

  const removeFilterRow = (index) => {
    const current = [...(values?.native?.dataFilter || [])];
    current.splice(index, 1);
    setFieldValue("native.dataFilter", current);
  };

  return (
    <>
      {_showBuilderSection("profile") && (
        <SectionRenderer
          title={t("triggers.builderSectionProfile", {
            defaultValue: "Overview",
          })}
          icon={AppAssets.icons.persona}
        >
          {isEdit && <IdCopyView id={getTriggerObject()?.id} />}

          <AppForms.AppFormField
            showTextInputLabel
            isMandatory
            wrapperInputStyle={{ paddingTop: 10 }}
            label={t("triggers.name")}
            placeholder={t("triggers.namePlaceholder")}
            keyboardtype="default"
            textContextType="label"
            autoCorrect={false}
            name="name"
          />
          <AppForms.AppFormField
            showTextInputLabel
            wrapperInputStyle={{ height: 150, marginBottom: 10, padding: 5 }}
            textContainer={{ padding: 10, marginHorizontal: 0, minHeight: 120 }}
            multiline
            label={t("settings.list.description")}
            placeholder={t("triggers.descriptionPlaceholder")}
            keyboardtype="default"
            textContextType="label"
            autoCorrect={false}
            name="description"
          />
        </SectionRenderer>
      )}
      {_showBuilderSection("execution") && (
        <SectionRenderer
          title={t("triggers.builderSectionExecution", {
            defaultValue: "Execution",
          })}
          icon={AppAssets.icons.bolt}
        >
          <AppForms.AppFormDropDown
            isElevated={false}
            dropDownStyle={{ borderColor: currentTheme?.theme?.border_color }}
            showTextInputLabel
            label={t("settings.list.agent")}
            isMandatory
            name="agentID"
            value={values?.agentID}
            items={[
              { id: "", name: t("settings.list.selectAgent") },
              ...agents.map((agent) => ({ id: agent.id, name: agent.label })),
            ]}
            propertyLabel="name"
            propertyValue="id"
          />

          {values?.forcedPrompt ? (
            <AppForms.AppFormField
              showTextInputLabel
              wrapperInputStyle={{ paddingTop: 10, padding: 5 }}
              multiline
              textContainer={{
                minHeight: 120,
                paddingVertical: 10,
                marginHorizontal: 0,
              }}
              label={t("settings.list.forcedPrompt")}
              placeholder={t("settings.list.forcedPromptPlaceholder")}
              keyboardtype="default"
              textContextType="label"
              autoCorrect={false}
              maxCharacters={1024}
              value={values?.forcedPrompt}
              name="forcedPrompt"
            />
          ) : values?.agentID && getAvailablePrompts().length === 0 ? (
            <View style={{ marginTop: 10, marginBottom: 20 }}>
              <AppText
                style={{ fontWeight: "bold", marginBottom: 10, marginLeft: 10 }}
              >
                {t("settings.list.prompt")}
              </AppText>
              <AppPlaceholderView
                wrapperStyle={{
                  marginLeft: 5,
                  minHeight: 150,
                  backgroundColor: currentTheme?.theme?.lightGrey,
                  borderRadius: 8,
                  padding: 20,
                  marginHorizontal: 5,
                  width: "auto",
                }}
                size={25}
                icon={AppAssets.icons.orbit}
                mainCaption={t("settings.list.noPromptsAvailable")}
                subCaption={t("settings.list.createPromptForAgent")}
                isClickable
                onPress={() => {
                  setDisplayedView("GENERATOR_SETTINGS");
                  history.push(
                    `/workspaces/${
                      getActiveWorkspace()?.id
                    }/settings/generator_settings`
                  );
                }}
              />
            </View>
          ) : (
            <AppForms.AppFormDropDown
              isElevated={false}
              dropDownStyle={{ borderColor: currentTheme?.theme?.border_color }}
              showTextInputLabel
              label={t("settings.list.prompt")}
              isMandatory
              name="customPromptID"
              value={values?.customPromptID}
              items={[
                {
                  id: "",
                  name: !values?.agentID
                    ? t("settings.list.selectAgentFirst")
                    : t("settings.list.selectPrompt"),
                },
                ...getAvailablePrompts().map((prompt) => ({
                  id: prompt.id,
                  name: prompt.interpolationString,
                })),
              ]}
              propertyLabel="name"
              propertyValue="id"
              disabled={!values?.agentID}
            />
          )}
        </SectionRenderer>
      )}
      {_showBuilderSection("event") && (
        <SectionRenderer
          title={t("triggers.builderSectionEvent", {
            defaultValue: "Event source",
          })}
          icon={AppAssets.icons.webhook}
        >
          <AppForms.AppFormDropDown
            isElevated={false}
            dropDownStyle={{ borderColor: currentTheme?.theme?.border_color }}
            showTextInputLabel
            label={t("triggers.eventSource")}
            isMandatory
            name="eventSourceType"
            value={values?.eventSourceType || "NATIVE"}
            items={TRIGGER_SOURCE_OPTIONS}
            propertyLabel="name"
            propertyValue="id"
          />

          {isNative ? (
            <View>
              <AppForms.AppFormDropDown
                isElevated={false}
                dropDownStyle={{
                  borderColor: currentTheme?.theme?.border_color,
                }}
                showTextInputLabel
                label={t("triggers.nativeEventType")}
                isMandatory
                name="native.eventType"
                value={values?.native?.eventType}
                items={nativeEvents.map((e) => ({
                  id: e.id,
                  name: `${e.label} (${e.id})`,
                }))}
                propertyLabel="name"
                propertyValue="id"
                _onValueChange={(val) => {
                  const evt = nativeEvents.find((e) => e.id === val);
                  setFieldValue("native.eventCategory", evt?.category || "");
                }}
              />

              <AppText
                style={{
                  fontWeight: "bold",
                  fontSize: 14,
                  marginTop: 15,
                  marginBottom: 5,
                  marginLeft: 10,
                }}
              >
                {t("triggers.dataFilter")}
              </AppText>
              <AppText
                leftIcon={AppAssets?.icons?.info}
                wrapperStyle={{ paddingHorizontal: 10 }}
                color={currentTheme?.theme?.orange}
                style={{ marginLeft: 10 }}
                iconSize={14}
              >
                {t("triggers.dataFilterHint")}
              </AppText>

              {(values?.native?.dataFilter || []).map((row, index) => (
                <View
                  key={index}
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 6,
                    marginRight: 10,
                    marginTop: 8,
                  }}
                >
                  <View style={{ flex: 1.4 }}>
                    <AppTextInput
                      showTextInputLabel={false}
                      placeholder={t("triggers.field")}
                      value={row?.field ?? ""}
                      onChangeText={(v) => updateFilterRow(index, "field", v)}
                    />
                  </View>
                  <View style={{ flex: 1 }}>
                    <AppDropDown
                      isElevated={false}
                      dropDownStyle={{
                        borderColor: currentTheme?.theme?.border_color,
                      }}
                      showTextInputLabel={false}
                      selectedValue={row?.op ?? "eq"}
                      items={FILTER_OPS}
                      propertyLabel="name"
                      propertyValue="id"
                      onValueChange={(v) => updateFilterRow(index, "op", v)}
                    />
                  </View>
                  <View style={{ flex: 1.2 }}>
                    <AppTextInput
                      showTextInputLabel={false}
                      placeholder={t("triggers.value")}
                      value={row?.value ?? ""}
                      onChangeText={(v) => updateFilterRow(index, "value", v)}
                    />
                  </View>
                  <AppIcon
                    icon={AppAssets?.icons?.close}
                    isClickable
                    size={16}
                    onPress={() => removeFilterRow(index)}
                    wrapperStyle={{ minWidth: 18, height: 40, marginBottom: 2 }}
                  />
                </View>
              ))}
              <ToothFairyButton
                title={t("triggers.addFilter")}
                onPress={addFilterRow}
                wrapperStyle={{ marginLeft: -5, marginTop: 10, minWidth: 120 }}
              />
            </View>
          ) : (
            <View>
              <AppForms.AppFormDropDown
                isElevated={false}
                dropDownStyle={{
                  borderColor: currentTheme?.theme?.border_color,
                }}
                showTextInputLabel
                label={t("triggers.provider")}
                isMandatory
                name="external.provider"
                value={values?.external?.provider}
                items={[
                  { id: "", name: t("triggers.selectProvider") },
                  ...providers.map((p) => ({
                    id: p.id,
                    name: `${p.label}${p.implemented ? "" : " (soon)"}`,
                  })),
                ]}
                propertyLabel="name"
                propertyValue="id"
                _onValueChange={(val) => {
                  const provider = providers.find((p) => p.id === val);
                  if (provider) {
                    setFieldValue("external.provider", val);
                    // Do NOT derive authorisationType from the catalog — it is set
                    // from the authorisation the user actually selects.
                    setFieldValue("external.acquisitionMode", "POLLING");
                    const firstEvent = provider.events[0]?.id || "";
                    setFieldValue("external.providerEvent", firstEvent);
                  }
                }}
              />

              {selectedProvider && !selectedProvider.implemented && (
                <View
                  style={{
                    marginHorizontal: 10,
                    marginBottom: 10,
                    padding: 10,
                    backgroundColor: currentTheme?.theme?.orange + "22",
                    borderRadius: 8,
                  }}
                >
                  <AppText
                    style={{ color: currentTheme?.theme?.orange, fontSize: 13 }}
                  >
                    {t("triggers.providerNotImplemented")}
                  </AppText>
                </View>
              )}

              <AppForms.AppFormDropDown
                isElevated={false}
                dropDownStyle={{
                  borderColor: currentTheme?.theme?.border_color,
                }}
                showTextInputLabel
                label={t("triggers.providerEvent")}
                isMandatory
                name="external.providerEvent"
                value={values?.external?.providerEvent}
                items={[
                  { id: "", name: t("triggers.selectEvent") },
                  ...providerEvents.map((e) => ({
                    id: e.id,
                    name: `${e.label} (${e.id})`,
                  })),
                ]}
                propertyLabel="name"
                propertyValue="id"
              />

              <AppForms.AppFormDropDown
                isElevated={false}
                dropDownStyle={{
                  borderColor: currentTheme?.theme?.border_color,
                }}
                showTextInputLabel
                label={t("triggers.authorisation")}
                isMandatory
                name="external.authorisationID"
                value={values?.external?.authorisationID}
                items={authorisationOptions}
                propertyLabel="name"
                propertyValue="id"
                _onValueChange={(val) => {
                  const auth = authorisations.find((a) => a?.id === val);
                  setFieldValue("external.authorisationID", val);
                  setFieldValue(
                    "external.authorisationType",
                    auth?.type || null
                  );
                }}
              />
              {values?.external?.provider && authorisations.length === 0 && (
                <AppText
                  leftIcon={AppAssets?.icons?.info}
                  wrapperStyle={{ paddingHorizontal: 10 }}
                  color={currentTheme?.theme?.orange}
                  style={{ marginLeft: 10 }}
                  iconSize={14}
                >
                  {t("triggers.createAuthorisationHint")}
                </AppText>
              )}

              <AppForms.AppFormDropDown
                isElevated={false}
                dropDownStyle={{
                  borderColor: currentTheme?.theme?.border_color,
                }}
                showTextInputLabel
                label={t("triggers.acquisitionMode")}
                name="external.acquisitionMode"
                value={values?.external?.acquisitionMode || "POLLING"}
                items={[
                  { id: "POLLING", name: t("triggers.polling") },
                  { id: "WEBHOOK", name: `${t("triggers.webhook")} (soon)` },
                ]}
                propertyLabel="name"
                propertyValue="id"
              />

              {(values?.external?.acquisitionMode || "POLLING") ===
                "POLLING" && (
                <AppForms.AppFormDropDown
                  isElevated={false}
                  dropDownStyle={{
                    borderColor: currentTheme?.theme?.border_color,
                  }}
                  showTextInputLabel
                  label={t("triggers.pollInterval")}
                  name="external.pollConfig.intervalSeconds"
                  value={values?.external?.pollConfig?.intervalSeconds || 300}
                  items={[
                    { id: 60, name: "1 min" },
                    { id: 120, name: "2 min" },
                    { id: 300, name: "5 min" },
                    { id: 600, name: "10 min" },
                    { id: 1800, name: "30 min" },
                  ]}
                  propertyLabel="name"
                  propertyValue="id"
                />
              )}

              {values?.external?.provider === "generic" && (
                <>
                  <AppForms.AppFormField
                    showTextInputLabel
                    wrapperInputStyle={{ paddingTop: 10 }}
                    label={t("triggers.pollEndpoint")}
                    placeholder="https://api.example.com/v1/items"
                    name="external.pollConfig.endpoint"
                  />
                  <AppForms.AppFormField
                    showTextInputLabel
                    wrapperInputStyle={{ paddingTop: 10 }}
                    label={t("triggers.jsonPath")}
                    placeholder="$.items[*]"
                    name="external.pollConfig.jsonPath"
                  />
                </>
              )}
            </View>
          )}
        </SectionRenderer>
      )}
      {_showBuilderSection("behaviour") && (
        <SectionRenderer
          title={t("triggers.builderSectionBehaviour", {
            defaultValue: "Behaviour",
          })}
          icon={AppAssets.icons.launch}
        >
          <AppForms.AppFormSwitch
            wrapperStyle={[
              {
                // backgroundColor: isEdit
                //   ? currentTheme?.theme?.grey
                //   : currentTheme?.theme?.lightGrey,
                maxHeight: 30,
                paddingHorizontal: 0,
                paddingVertical: 18,
                margin: 5,
                marginTop: 10,
                marginBottom: 20,
                marginRight: 5,
                width: "auto",
              },
            ]}
            labelTextStyle={{
              fontSize: currentTheme?.theme?.small_font_size,
              color: currentTheme?.theme?.darkGrey,
            }}
            showTextInputLabel
            label={t("settings.list.active")}
            displayIsTicked
            name="isActive"
            value={values?.isActive}
            description={t("settings.list.activeDesc")}
            descriptionIcon={false}
            descriptionColor={currentTheme?.theme?.mediumGrey}
          />
        </SectionRenderer>
      )}
      {!isBuilderLayout && (
        <View style={{ alignItems: "center", zIndex: -1 }}>
          <AppForms.AppSubmitButton
            title={_title()}
            awesomeIconColor={currentTheme?.theme?.white}
            backgroundColor={currentTheme?.theme?.primary}
            btnContentStyle={{ paddingLeft: 0, paddingRight: 0 }}
            style={[
              {
                height: Constants.appButtonHeight,
                width: "auto",
                shadowColor: currentTheme?.theme?.transparent,
                marginHorizontal: 10,
              },
            ]}
            wrapperStyle={[
              {
                width: "fit-content",
                minWidth: 150,
                marginTop: 20,
                height: Constants.appButtonHeight,
              },
            ]}
          />
          {isEdit && (
            <ToothFairyButton
              title={t("settings.list.delete")}
              onPress={() => handleDeletion(getTriggerObject()?.id)}
              isDeletion
              wrapperStyle={{
                marginTop: 30,
                minWidth: 150,
                height: Constants.appButtonHeight,
              }}
            />
          )}
        </View>
      )}
    </>
  );
};

export default TriggerSettingsView;
